import React, { useContext, useEffect, useState } from "react";
import { Context } from "../../index";
import { observer } from "mobx-react-lite";
import UniversalTable from "../../components/organization/table/UniTable";
import CreateUserForm from "../../components/loginform/CreateUserForm";
import PageHeader from "../../components/header/pageheader/PageHeader";
import Modal from "../../components/forms/wrap/Modal";
import apiService from "../../services/apiService";
import {
  accountBlockErrorText,
  blockRequestOf,
  isBlockedAccount,
} from "../../utils/accountBlock";
import "./styles.css";

interface User {
  id: number;
  email: string;
  tel: string;
  password?: string;
  roleId: number;
  companyId: number;
  plantId: number | null;
  /** `К2.1`: из `UserDto` — организация и состояние учётной записи. */
  company_id?: number | null;
  account_state?: string | null;
  role?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  surName?: string | null;
}

/** Имя для окна подтверждения: ФИО, иначе почта. */
function nameOf(user: User | null): string {
  if (!user) return "";

  const full = [user.lastName, user.firstName, user.surName]
    .map((part) => (part || "").trim())
    .filter(Boolean)
    .join(" ");

  return full || user.email || `№${user.id}`;
}

export default observer(function Users() {
  const { store } = useContext(Context);

  const [isvisible, setvisible] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);

  // `К2.1` (`АРХ-Н16`): блокировка — через окно подтверждения с причиной.
  const [blockTarget, setBlockTarget] = useState<User | null>(null);
  const [blockReason, setBlockReason] = useState("");
  const [blockError, setBlockError] = useState<string | null>(null);
  const [blockBusy, setBlockBusy] = useState(false);

  useEffect(() => {
    fetchUsers();
  }, []);

  // `п. 39`: ссылка из колонки «Владелец» ведёт на карточку человека —
  // `/users?userId=N`. Открываем её, когда список уже загружен: карточку
  // показывает тот же обработок, что и нажатие строки.
  useEffect(() => {
    const wanted = Number(
      new URLSearchParams(window.location.search).get("userId"),
    );

    if (!Number.isInteger(wanted) || wanted < 1) return;

    const user = store.users.find((item: User) => Number(item.id) === wanted);

    if (user) handleEdit(user);
  }, [store.users]);

  const fetchUsers = () => {
    store.fetchUsers();
  };

  const handleEdit = (user: User) => {
    // `АУД-Н156`: запись пользователя в консоль не пишем — это персональные данные.
    setEditingUser(user);
    setvisible(true);
  };

  const openBlockDialog = (user: User) => {
    setBlockTarget(user);
    setBlockReason("");
    setBlockError(null);
  };

  const closeBlockDialog = () => {
    if (blockBusy) return;

    setBlockTarget(null);
    setBlockReason("");
    setBlockError(null);
  };

  const submitBlock = async () => {
    if (!blockTarget || blockBusy) return;

    const blocked = isBlockedAccount(blockTarget.account_state);
    const action = blocked ? "unblock" : "block";

    setBlockBusy(true);
    setBlockError(null);

    try {
      const response = await apiService.POST<{ state?: string }>(
        `/users/${blockTarget.id}/${action}`,
        blockRequestOf(blockReason),
      );

      // Ответ back называет новое состояние: `blocked` или прежнее при возврате.
      const nextState =
        response.data?.state || (blocked ? "active" : "blocked");

      store.setUserAccountState(blockTarget.id, nextState);
      setBlockTarget(null);
      setBlockReason("");
    } catch (error: any) {
      // `К2.1`: отказ back (400/403/404) — его текст, окно остаётся открытым.
      setBlockError(accountBlockErrorText(error));
    } finally {
      setBlockBusy(false);
    }
  };

  const users = store.users;
  const itemsToDisplay = users;

  return (
    <>
      <div className="all_station_container">
        <PageHeader
          title="ПОЛЬЗОВАТЕЛИ"
          buttontext="Создать пользователя"
          onCreate={() => {
            setEditingUser(null);
            setvisible(true);
          }}
        />

        <UniversalTable
          data={itemsToDisplay}
          type="users"
          Edit={handleEdit}
          onAccountBlock={openBlockDialog}
        />
      </div>

      <Modal
        isOpen={isvisible}
        onClose={() => {
          setvisible(false);
          setEditingUser(null);
        }}
      >
        <CreateUserForm
          close={() => {
            setvisible(false);
            setEditingUser(null);
          }}
          user={editingUser}
        />
      </Modal>

      <Modal isOpen={blockTarget !== null} onClose={closeBlockDialog}>
        <div className="account-block-dialog">
          <h2 className="account-block-dialog__title">
            {blockTarget && isBlockedAccount(blockTarget.account_state)
              ? "Разблокировать"
              : "Заблокировать"}{" "}
            {nameOf(blockTarget)}?
          </h2>
          <p className="account-block-dialog__text">
            {blockTarget && isBlockedAccount(blockTarget.account_state)
              ? "Вход снова откроется в прежнем состоянии, данные сохранятся."
              : "Вход будет закрыт, данные сохранятся."}
          </p>

          <label className="account-block-dialog__field">
            <span>Причина</span>
            <textarea
              aria-label="Причина"
              value={blockReason}
              onChange={(event) => setBlockReason(event.target.value)}
              rows={3}
            />
          </label>

          {blockError ? (
            <p className="account-block-dialog__error" role="alert">
              {blockError}
            </p>
          ) : null}

          <div className="account-block-dialog__actions">
            <button
              type="button"
              className="account-block-dialog__cancel"
              onClick={closeBlockDialog}
              disabled={blockBusy}
            >
              Отмена
            </button>
            <button
              type="button"
              className="account-block-dialog__submit"
              onClick={submitBlock}
              disabled={blockBusy}
            >
              {blockTarget && isBlockedAccount(blockTarget.account_state)
                ? "Разблокировать"
                : "Заблокировать"}
            </button>
          </div>
        </div>
      </Modal>
    </>
  );
});
